import { useState, useEffect } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { BottomNavigation, BottomNavigationAction, Paper, Chip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import StorageIcon from '@mui/icons-material/Storage'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InfoIcon from '@mui/icons-material/Info'
import SearchPage from './pages/SearchPage'
import DatabasePage from './pages/DatabasePage'
import SubmitPage from './pages/SubmitPage'
import ComparePage from './pages/ComparePage'
import AboutPage from './pages/AboutPage'
import api from './services/api'

function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const [value, setValue] = useState(0)
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)

  // Sync navigation state with current route
  useEffect(() => {
    const pathMap = {
      '/': 0,
      '/database': 1,
      '/submit': 2,
      '/about': 3,
    }
    setValue(pathMap[location.pathname] ?? 0)
  }, [location.pathname])

  // Fetch statistics on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const cameras = await api.getCameras()

        // Calculate statistics by manufacturer
        const manufacturerCounts = cameras.reduce((acc, camera) => {
          acc[camera.manufacturer] = (acc[camera.manufacturer] || 0) + 1
          return acc
        }, {})

        // Convert to array and sort by count (descending)
        const statsArray = Object.entries(manufacturerCounts)
          .map(([manufacturer, count]) => ({ manufacturer, count }))
          .sort((a, b) => b.count - a.count)

        setStats(statsArray)
        setLoading(false)
      } catch (error) {
        console.error('Failed to fetch statistics:', error)
        // Don't block the app if stats fail
        setStats([])
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const handleNavigationChange = (event, newValue) => {
    setValue(newValue)
    const paths = ['/', '/database', '/submit', '/about']
    navigate(paths[newValue])
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 animate-gradient">
      {/* Main content area with bottom padding for navigation and stats bar */}
      <div className="pb-32 min-h-screen">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<SearchPage />} />
            <Route path="/database" element={<DatabasePage />} />
            <Route path="/submit" element={<SubmitPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/about" element={<AboutPage />} />
          </Routes>
        </AnimatePresence>
      </div>

      {/* Statistics Bar */}
      {!loading && stats.length > 0 && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 56, // Height of bottom navigation
            left: 0,
            right: 0,
            zIndex: 999,
            background: 'rgba(10, 10, 10, 0.9)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(59, 130, 246, 0.1)',
            padding: '8px 16px',
          }}
          elevation={2}
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 20 }}
            className="flex items-center justify-center gap-3 flex-wrap"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.manufacturer}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <Chip
                  label={`${stat.manufacturer}: ${stat.count} camera${stat.count !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    color: '#60a5fa',
                    fontWeight: 'medium',
                    fontSize: '0.75rem',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.25)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </Paper>
      )}

      {/* Bottom Navigation */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'rgba(26, 26, 26, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
        }}
        elevation={3}
      >
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        >
          <BottomNavigation
            value={value}
            onChange={handleNavigationChange}
            showLabels
            sx={{
              backgroundColor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                color: 'rgba(255, 255, 255, 0.5)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'rgba(59, 130, 246, 0.8)',
                  transform: 'translateY(-2px)',
                },
                '&.Mui-selected': {
                  color: '#3b82f6',
                  '& .MuiSvgIcon-root': {
                    transform: 'scale(1.2)',
                  },
                },
              },
            }}
          >
            <BottomNavigationAction
              label="Search"
              icon={<SearchIcon />}
            />
            <BottomNavigationAction
              label="Database"
              icon={<StorageIcon />}
            />
            <BottomNavigationAction
              label="Submit"
              icon={<CloudUploadIcon />}
            />
            <BottomNavigationAction
              label="About"
              icon={<InfoIcon />}
            />
          </BottomNavigation>
        </motion.div>
      </Paper>
    </div>
  )
}

export default App
